import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Interface for container props to handle transition states
interface ContainerProps {
  showPrefix: boolean;
  isTypingComplete: boolean;
}

const Container = styled.div<ContainerProps>`
  font-size: 3rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 20%;
  width: 100%;
  position: absolute;
  top: 35%;
  transform: translateY(-50%);
  /* This will inherit from the global styles */
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  color: ${props => props.isTypingComplete ? '#3E3E3E' : 'white'};
  transition: color 0.5s ease;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    padding-left: 10%;
  }
`;

const Cursor = styled.span`
  color: ${props => props.theme === 'light' ? '#3E3E3E' : 'white'};
  transition: color 0.5s ease;
`;

// Make onComplete optional with a default no-op function
interface TypingAnimationProps {
  onComplete?: () => void;
}

const TypingAnimation: React.FC<TypingAnimationProps> = ({ 
  onComplete = () => {} // Default no-op function
}) => {
  const [text, setText] = useState('');
  const prefixText = 'Hello ';
  const worldText = 'World!';
  const fullText = prefixText + worldText;
  const [isTypingWorld, setIsTypingWorld] = useState(true);
  const [showPrefix, setShowPrefix] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  
  // Use a more precise timing mechanism with requestAnimationFrame
  useEffect(() => {
    let lastTimestamp = 0;
    let animationFrameId: number;
    let charInterval = 120; // Milliseconds between characters - more consistent
    
    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      
      const elapsed = timestamp - lastTimestamp;
      
      // First phase: Type out the full "Hello World"
      if (text.length < fullText.length && isTypingWorld) {
        if (elapsed > charInterval) {
          setText(fullText.substring(0, text.length + 1));
          lastTimestamp = timestamp;
          
          // Once we finish typing "Hello ", set the showPrefix flag
          if (text.length + 1 === prefixText.length) {
            setShowPrefix(true);
          }
        }
      } 
      // Pause at the end of typing
      else if (text.length === fullText.length && isTypingWorld) {
        if (elapsed > 1000) { // 1 second pause
          setIsTypingWorld(false);
          setIsTypingComplete(true);
          lastTimestamp = timestamp;
        }
      } 
      // Only backspace the "World" part
      else if (!isTypingWorld && text.length > prefixText.length) {
        if (elapsed > 80) { // Slightly faster for backspacing
          setText(text.substring(0, text.length - 1));
          lastTimestamp = timestamp;
        }
      } 
      // When just "Hello " remains, transition to Earth
      else if (!isTypingWorld && text.length === prefixText.length) {
        setTimeout(() => {
          onComplete();
        }, 500); // Small delay before transitioning
        return; // Stop the animation loop
      }
      
      // Continue the animation
      animationFrameId = requestAnimationFrame(animate);
    };
    
    // Start the animation
    animationFrameId = requestAnimationFrame(animate);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [text, isTypingWorld, onComplete, fullText, prefixText]);
  
  return (
    <Container 
      showPrefix={showPrefix} 
      isTypingComplete={isTypingComplete}
    >
      {text}
      <Cursor 
        className="cursor" 
        theme={isTypingComplete ? 'light' : 'dark'}
      >|</Cursor>
    </Container>
  );
};

export default TypingAnimation;